'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organization: string;
  role: string;
  teamSize: string;
  message: string;
}

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  organization: '',
  role: '',
  teamSize: '',
  message: '',
};

export default function DemoRequestForm() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { trackForm } = useAnalytics();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, '');
      
      // Format the phone number as (XXX) XXX-XXXX
      let formattedValue = '';
      if (numericValue.length <= 3) {
        formattedValue = numericValue;
      } else if (numericValue.length <= 6) {
        formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
      } else {
        formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission
    trackForm('demo_request', {
      organization_type: formData.role,
      team_size: formData.teamSize,
    });

    try {
      // Send form data to API
      const response = await fetch('/api/demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      // Reset form and show success message
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      alert('There was an error submitting your request. Please try again later.');
    }
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
      {!isSubmitted ? (
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          onSubmit={handleSubmit}
          className="p-6 sm:p-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6">Request a Demo</h2>
          <p className="text-gray-400 mb-8">
            Fill out the form below and one of our sports management experts will contact you to schedule a personalized demo.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-1">
              Organization Name *
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-1">
                Your Role *
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              >
                <option value="">Select your role</option>
                <option value="coach">Coach</option>
                <option value="admin">Administrator</option>
                <option value="director">Program Director</option>
                <option value="parent">Parent</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="teamSize" className="block text-sm font-medium text-gray-300 mb-1">
                Team/Organization Size *
              </label>
              <select
                id="teamSize"
                name="teamSize"
                value={formData.teamSize}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              >
                <option value="">Select size</option>
                <option value="1-10">1-10 members</option>
                <option value="11-50">11-50 members</option>
                <option value="51-100">51-100 members</option>
                <option value="101-500">101-500 members</option>
                <option value="500+">500+ members</option>
              </select>
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
              Additional Information
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 bg-gray-800/50 border border-white/10 rounded-md text-white placeholder-gray-400 focus:ring-futuristic-neon focus:border-futuristic-neon"
              placeholder="Tell us about your specific needs or questions"
            />
          </div>

          <Button
            type="submit"
            variant="neon"
            size="lg"
            className="w-full bg-futuristic-neon hover:bg-futuristic-neon/90 text-black font-medium"
            disabled={isSubmitting}
            trackingName="demo_form_submit"
            trackingLocation="demo_page"
          >
            {isSubmitting ? 'Submitting...' : 'Request Your Demo'}
          </Button>
          
          <p className="text-sm text-gray-400 mt-4">
            By submitting this form, you agree to our{' '}
            <a href="/privacy" className="text-futuristic-neon hover:text-futuristic-neon/80 transition-colors">
              Privacy Policy
            </a>
            .
          </p>
        </motion.form>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="p-8 text-center"
        >
          <div className="w-16 h-16 bg-futuristic-neon/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-futuristic-neon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Thank You!</h2>
          <p className="text-gray-300 mb-6">
            Your demo request has been submitted successfully. One of our team members will contact you shortly to schedule your personalized demo.
          </p>
          <Button
            variant="neon"
            href="/"
            className="bg-futuristic-neon hover:bg-futuristic-neon/90 text-black font-medium"
            trackingName="demo_success_home_button"
            trackingLocation="demo_page"
          >
            Return to Homepage
          </Button>
        </motion.div>
      )}
    </div>
  );
} 